<template>
  <VideoModal
    :isOpen="isVideoOpen"
    @closeModal="closeVideo"
    :title="$t('rc_promo_video_header')"
    itemKey="rateConnect"
    id="Rate Connect"
  />
  <div class="tw:pt-6">
    <div class="tw:flex tw:justify-between tw:w-full">
      <div class="tw:flex tw:flex-row tw:items-center">
        <div
          class="tw:mr-2 tw:flex tw:items-center tw:justify-center tw:rounded-full tw:border-4 tw:h-[60px] tw:w-[60px] tw:min-w-[60px]"
          :class="statusColors"
        >
          <span
            class="tw:flex tw:items-center tw:justify-center tw:rounded-full tw:border tw:h-[48px] tw:w-[48px] tw:border-gray-300"
          >
            <img :src="RateConnectIcon" :alt="$t('app.rateConnect.name')" class="tw:w-[18px] tw:h-[20px]" />
          </span>
        </div>
        <div class="tw:flex tw:flex-col">
          <h3 class="tw:text-2xl tw:font-bold">{{ $t('app.rateConnect.name') }}</h3>
          <div class="tw:flex tw:gap-x-3 tw:flex-col tw:md:flex-row">
            <p>{{ $t('rc_status_bar_subtext') }}</p>
            <div class="tw:flex tw:gap-x-3 tw:text-sm tw:items-center">
              <div
                v-if="showVideoButton && campaignType === 'netCpa' && !isMobile && !isTourCompleted"
                class="tw:h-[12px] tw:border-r tw:border-black"
              />
              <button
                v-if="showVideoButton && campaignType === 'netCpa' && !isMobile && !isTourCompleted"
                @click="beginTour"
                class="tw:text-blue-700 tw:font-bold tw:hover:cursor-pointer tw:hover:underline"
              >
                {{ $t('rc_tour_start_cta') }}
              </button>
              <div
                v-if="showVideoButton && campaignType === 'netCpa' && !isMobile"
                class="tw:h-[12px] tw:border-r tw:border-black"
              />
              <button
                v-if="showVideoButton"
                @click="openVideo"
                class="tw:text-blue-700 tw:font-bold tw:hover:cursor-pointer tw:hover:underline"
              >
                {{ $t('rc_welcome_bannner_video_cta') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'

import VideoModal from '@/components/rateConnect/VideoModal.vue'
import appAnalytics from '@/utils/tracking'

import { storageSession } from '@/utils/storageService'
import { selectedAccommodationId, useUpdatePromotionStatus, useUserDetails } from '@/layouts/queries'
import { useTour } from '@/composables/useRateConnectTour'
import { useIsMobile } from '@/composables/useIsMobile'
import { RATE_CONNECT_TOUR_STEPS } from '@/constants/rateConnectTourSteps.js'
import { useSessionValues } from '@/composables/useSessionValues'

const RateConnectIcon = new URL(`../../../assets/img/rate-connect/rate-connect-icon.png`, import.meta.url)

const props = defineProps({
  status: String,
  campaignType: String,
})

const { userId } = useSessionValues()
const { isMobile } = useIsMobile()
const { startTour } = useTour()
const { updatePromotionStatus } = useUpdatePromotionStatus(userId)
const { userDetails } = useUserDetails(userId)
const showVideoButton = ref(true)
let cleanupListener = null
const isVideoOpen = ref(false)
const hasMarkedSeen = ref(false)
const statusColors = computed(() => {
  if (props.status === 'success') return 'tw:border-green-400'
  else if (props.status === 'warning') return 'tw:border-orange-400'
  else return 'tw:border-red-400'
})

const rateConnectTourData = computed(() =>
  userDetails.value?.promotions?.find(item => item.promotionName === 'RC_PRODUCT_INTRO_TOUR_V1')
)

const isTourCompleted = computed(() => rateConnectTourData.value?.isCompleted ?? false)

const openVideo = () => {
  appAnalytics.track(appAnalytics.events.RC_STATUS_BAR_PLAY_VIDEO_CLICKED, {
    itemId: selectedAccommodationId.value,
  })
  isVideoOpen.value = true
}
const closeVideo = () => {
  appAnalytics.track(appAnalytics.events.RC_WELCOME_VIDEO_CLOSED, {
    itemId: selectedAccommodationId.value,
  })
  isVideoOpen.value = false
}

watch(
  userDetails,
  details => {
    if (hasMarkedSeen.value || !details) return

    const tourData = details.promotions?.find(item => item.promotionName === 'RC_PRODUCT_INTRO_TOUR_V1')
    const isCompleted = tourData?.isCompleted ?? false
    const isSeen = !!tourData?.firstSeenAt

    if (showVideoButton.value && props.campaignType === 'netCpa' && !isMobile.value && !isCompleted && !isSeen) {
      hasMarkedSeen.value = true
      updatePromotionStatus({
        promotionType: 'RC_PRODUCT_INTRO_TOUR_V1',
        status: 'SEEN',
      })
    }
  },
  { immediate: true }
)

onMounted(() => {
  const value = storageSession.getItem('new_campaign_created')
  showVideoButton.value = value !== 'true'

  cleanupListener = storageSession.onChange('new_campaign_created', newValue => {
    showVideoButton.value = newValue !== 'true'
  })
})

onBeforeUnmount(() => {
  if (cleanupListener) {
    cleanupListener()
  }
})

const beginTour = () => {
  appAnalytics.track(appAnalytics.events.RC_TAKE_TOUR_CLICKED, {
    itemId: selectedAccommodationId.value,
  })
  updatePromotionStatus({
    promotionType: 'RC_PRODUCT_INTRO_TOUR_V1',
    status: 'STARTED',
  })
  startTour(RATE_CONNECT_TOUR_STEPS)
}
</script>
